import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import LanguageToggle from '../components/i18n/LanguageToggle';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [form, setForm] = useState({ full_name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError(t({ fr: 'Les mots de passe ne correspondent pas', en: 'Passwords do not match' })); return; }
    if (form.password.length < 6) { setError(t({ fr: 'Le mot de passe doit contenir au moins 6 caractères', en: 'Password must be at least 6 characters' })); return; }
    setLoading(true);
    try {
      await signup(form.full_name, form.email, form.password);
      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4 z-10"><LanguageToggle variant="light" /></div>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <span className="text-2xl font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>SELORA</span>
          </Link>
          <p className="text-sm text-[#6B4E37] mt-2">{t({ fr: "Commencez votre parcours d'expression française dès aujourd'hui.", en: 'Start your French speaking journey today.' })}</p>
        </div>

        {success ? (
          <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DC] p-8 text-center" data-testid="signup-success">
            <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t({ fr: 'Votre compte a été créé avec succès.', en: 'Your account has been created successfully.' })}</h2>
            <p className="text-sm text-[#6B4E37]">{t({ fr: 'Redirection vers votre tableau de bord...', en: 'Redirecting to your dashboard...' })}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-[#E8E4DC] p-8">
            <h1 className="text-2xl font-bold text-[#1A2F4A] text-center mb-6" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="signup-title">
              {t({ fr: 'Créer un compte gratuit', en: 'Create a Free Account' })}
            </h1>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5" data-testid="signup-error">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Nom complet', en: 'Full Name' })}</label>
                <input type="text" value={form.full_name} onChange={set('full_name')} required placeholder={t({ fr: 'Entrez votre nom complet', en: 'Enter your full name' })}
                  className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                  data-testid="signup-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Adresse e-mail', en: 'Email Address' })}</label>
                <input type="email" value={form.email} onChange={set('email')} required placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                  data-testid="signup-email" />
              </div>
              <div className="relative">
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Mot de passe', en: 'Password' })}</label>
                <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={set('password')} required placeholder={t({ fr: 'Au moins 6 caractères', en: 'At least 6 characters' })}
                  className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8] pr-12"
                  data-testid="signup-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-9 text-[#999] hover:text-[#1A2F4A]">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A2F4A] mb-1.5">{t({ fr: 'Confirmer le mot de passe', en: 'Confirm Password' })}</label>
                <input type="password" value={form.confirm} onChange={set('confirm')} required placeholder={t({ fr: 'Ressaisissez votre mot de passe', en: 'Re-enter your password' })}
                  className="w-full px-4 py-3 border border-[#E8E4DC] rounded-xl focus:outline-none focus:border-[#D4AF37] text-[#1A2F4A] bg-[#FAFAF8]"
                  data-testid="signup-confirm" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full bg-[#1A2F4A] hover:bg-[#243a58] text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="signup-submit">
                {loading ? t({ fr: 'Création du compte...', en: 'Creating Account...' }) : t({ fr: 'Créer un compte', en: 'Create Account' })}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <div className="my-6 flex items-center gap-3">
              <div className="flex-1 h-px bg-[#E8E4DC]" /><span className="text-xs text-[#999]">{t({ fr: 'ou', en: 'or' })}</span><div className="flex-1 h-px bg-[#E8E4DC]" />
            </div>

            <div className="space-y-3">
              <GoogleSignInButton />
            </div>

            <p className="text-center text-sm text-[#6B4E37] mt-6">
              {t({ fr: 'Vous avez déjà un compte ?', en: 'Already have an account?' })} <Link to="/login" className="text-[#1A2F4A] font-semibold hover:text-[#D4AF37] transition-colors">{t({ fr: 'Se connecter', en: 'Log in' })}</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
